import { Card, Colors, Heading, Icon, Pills, Text } from '@amelio/ui-library';
import { Fragment, useEffect, useState } from 'react';

import { CompetencyTranslationRow } from './CompetencyTranslationRow';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';
import type { LocalizedText } from '@/utils/localization';

import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { useAmelioTranslations } from '@/hooks';
import { getLanguageDisplayName, getTextByLanguage } from '@/utils/localization';

// The shared field-kind enum has no profile kind, so profile edits carry a local descriptor.
export type ProfileTranslationField = 'name' | 'description';

export interface ProfileTranslationChange {
    profileId: string;
    field: ProfileTranslationField;
    value: string;
}

interface ProfilesTranslationAccordionProps {
    profiles: CompetencyProfile[];
    sourceLanguage: string;
    targetLanguage: string;
    onChange: (change: ProfileTranslationChange) => void;
}

const countProfileProgress = (profiles: CompetencyProfile[], language: string) => {
    let translated = 0;
    let total = 0;

    const count = (text: LocalizedText) => {
        total += 1;
        if ((text[language] ?? '').trim().length > 0) {
            translated += 1;
        }
    };

    profiles.forEach((profile) => {
        count(profile.name);
        count(profile.description);
    });

    return { translated, total };
};

export const ProfilesTranslationAccordion = ({
    profiles,
    sourceLanguage,
    targetLanguage,
    onChange,
}: ProfilesTranslationAccordionProps) => {
    const { t } = useAmelioTranslations();

    const progress = countProfileProgress(profiles, targetLanguage);
    const isComplete = progress.total > 0 && progress.translated === progress.total;

    const [isOpen, setIsOpen] = useState<boolean>(!isComplete);

    useEffect(() => {
        if (!isComplete) {
            setIsOpen(true);
        }
    }, [isComplete]);

    const source = (text: LocalizedText) => getTextByLanguage(text, sourceLanguage);
    const target = (text: LocalizedText) => text[targetLanguage] ?? '';

    return (
        <Card
            borderRadius={'2xl'}
            padding={'none'}
            className={'overflow-hidden'}
        >
            <button
                type={'button'}
                onClick={() => setIsOpen((open) => !open)}
                className={
                    'flex w-full flex-col items-center justify-between gap-2 border border-gray-100 bg-purple-50 px-6 py-4 text-left sm:flex-row'
                }
                aria-expanded={isOpen}
                data-testid={'translation-profiles-accordion'}
            >
                <Heading
                    level={5}
                    weight={'semibold'}
                    className={'text-md'}
                >
                    {t('competenciesTranslations.profilesSection')}
                </Heading>

                <div className={'flex items-center gap-3'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'text-caption whitespace-nowrap'}
                        data-testid={'translation-profiles-count'}
                    >
                        {t('competenciesTranslations.fieldsTranslated', {
                            translated: progress.translated,
                            total: progress.total,
                        })}
                    </Text>

                    <Icon
                        name={isComplete ? 'essential-tick-circle' : 'essential-danger'}
                        color={isComplete ? Colors.SUCCESS : Colors.WARNING}
                    />

                    <Icon
                        name={'arrow-arrow-down'}
                        className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
                    />
                </div>
            </button>

            {isOpen && (
                <div className={'bg-white/80 px-6 py-4'}>
                    <div className={'border-base-100 text-caption flex gap-6 border-b pb-2'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption flex-1 uppercase'}
                        >
                            {getLanguageDisplayName(sourceLanguage)}
                        </Text>

                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption flex-1 uppercase'}
                        >
                            {sourceLanguage !== targetLanguage
                                ? getLanguageDisplayName(targetLanguage)
                                : ''}
                        </Text>
                    </div>

                    {profiles.map((profile) => (
                        <Fragment key={profile.id}>
                            <div className={'flex items-center gap-3 pt-3'}>
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'text-caption'}
                                >
                                    {source(profile.name)}
                                </Text>

                                {!profile.isActive && (
                                    <Pills size={'sm'}>
                                        <Text size={'small'}>{t('common.deactivated')}</Text>
                                    </Pills>
                                )}
                            </div>

                            <CompetencyTranslationRow
                                variant={CompetencyTranslationFieldKind.COMPETENCY_NAME}
                                sourceText={source(profile.name)}
                                targetValue={target(profile.name)}
                                onTargetChange={(value) =>
                                    onChange({ profileId: profile.id, field: 'name', value })
                                }
                            />

                            <CompetencyTranslationRow
                                variant={CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION}
                                sourceText={source(profile.description)}
                                targetValue={target(profile.description)}
                                onTargetChange={(value) =>
                                    onChange({ profileId: profile.id, field: 'description', value })
                                }
                            />
                        </Fragment>
                    ))}
                </div>
            )}
        </Card>
    );
};
